import type { Employee } from "@pentacode/core";
import type { AnyRouter } from "@tanstack/router-core";
import { type TemplateResult, html } from "lit";
import { canAccess } from "../../../lib/route-tree";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { type AppRootRoute, type AppRouterContext, redirectTo } from "../../app-router-context";
import { type EmployeePage, employeePages, employeeRoute } from "../../app-routes";
import { routeAccessOf } from "../../route-access";
import { createEmployeeBalancesRoutes } from "./balances.routes";
import { createEmployeeCalendarRoutes } from "./calendar.routes";
import { createEmployeeProfileRoutes } from "./profile.routes";
import { createEmployeeTimeRoutes } from "./time.routes";

const LAYOUT_ID = "/employees/$employeeId";

function isPage(page: string): page is EmployeePage {
    return (employeePages as string[]).includes(page);
}

function access({ app, embedded }: AppRouterContext, employee: Employee) {
    return routeAccessOf(app, embedded, employee);
}

/** The child page ids under `employees/$employeeId` that have menu entries, read from the route tree itself. */
export function employeeMenuPages(router: AnyRouter) {
    return (
        router.routesById[LAYOUT_ID as never] as {
            children?: { path: string; options: { staticData?: { menu?: unknown } } }[];
        }
    )
        .children!.filter((child) => child.options.staticData?.menu)
        .map((child) => child.path.split("/")[0] as EmployeePage)
        .filter((page, i, pages) => pages.indexOf(page) === i)
        .sort((a, b) => employeePages.indexOf(a) - employeePages.indexOf(b));
}

export function createEmployeeLayoutRoute(parent: AppRootRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "employees/$employeeId",
        params: {
            parse: ({ employeeId }: { employeeId: string }) => ({ employeeId: Number(employeeId) }),
            stringify: ({ employeeId }: { employeeId: number }) => ({ employeeId: String(employeeId) }),
        },
        beforeLoad: ({ params, location, context }): void => {
            const employee = context.app.getEmployee(params.employeeId);
            if (!employee) {
                redirectTo("/employees/all");
            }

            const base = `/employees/${params.employeeId}`;
            const page = location.pathname.slice(base.length + 1).split("/")[0];

            if (!context.app.hasPermissionForEmployee(employee!)) {
                if (page !== "unauthorized") {
                    redirectTo(`${base}/unauthorized`, {}, false);
                }
                return;
            }

            if (!isPage(page) || !canAccess(employeeRoute(page), access(context, employee!))) {
                if (page && page !== "unauthorized") {
                    context.reportDenied(location.pathname.slice(1));
                }
                const fallback = ["core-data" as const, ...employeePages].find((p) =>
                    canAccess(employeeRoute(p), access(context, employee!))
                );
                if (fallback) {
                    redirectTo(`${base}/${fallback}`);
                }
            }
        },
        component: ({ match, router }): TemplateResult => {
            if ((router.options.context as AppRouterContext).mode === "view") {
                return html`<ptc-route-outlet></ptc-route-outlet>`;
            }
            const base = `/employees/${match.params.employeeId}`;
            const leaf = router.stores.matches.get().at(-1);
            return html`<ptc-employees-single
                .employeeId=${match.params.employeeId}
                .page=${leaf?.pathname.slice(base.length + 1).split("/")[0] ?? ""}
                .search=${router.stores.location.get().search}
                .pages=${employeeMenuPages(router)}
                ?loading=${router.stores.__store.get().isLoading}
                ><slot name="submenu" slot="submenu"></slot
            ></ptc-employees-single>`;
        },
    }).lazy(lazyElement(() => import("./index")));
}

export type EmployeeLayoutRoute = ReturnType<typeof createEmployeeLayoutRoute>;

export function createEmployeeRoute(parent: AppRootRoute) {
    const layout = createEmployeeLayoutRoute(parent);
    // The shell shows the "outside your permissions" notice for this page.
    const unauthorized = createRoute({ getParentRoute: () => layout, path: "unauthorized" });
    // Unknown pages must still match below the layout, so its beforeLoad redirects them.
    const unknownPage = createRoute({ getParentRoute: () => layout, path: "$" });
    return layout.addChildren([
        ...createEmployeeTimeRoutes(layout),
        ...createEmployeeBalancesRoutes(layout),
        ...createEmployeeCalendarRoutes(layout),
        ...createEmployeeProfileRoutes(layout),
        unauthorized,
        unknownPage,
    ]);
}
